import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Plus, FileText, Clock, CheckCircle, PaperPlaneTilt, ArrowRight } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  draft:            { label: 'Drafts',         color: '#64748b', bg: '#f1f5f9', icon: FileText },
  in_review:        { label: 'In Review',      color: '#0369a1', bg: '#e0f2fe', icon: Clock },
  ready_for_export: { label: 'Ready',          color: '#7c3aed', bg: '#ede9fe', icon: CheckCircle },
  sent_to_client:   { label: 'Sent to Client', color: '#d97706', bg: '#fef3c7', icon: PaperPlaneTilt },
  approved:         { label: 'Approved',       color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
};

const ProposalsStaffOverviewPage = () => {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/proposals`, { withCredentials: true });
      setProposals(r.data || []);
    } catch (_) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // counts by status
  const counts = useMemo(() => {
    const c = { draft: 0, in_review: 0, ready_for_export: 0, sent_to_client: 0, approved: 0 };
    proposals.forEach((p) => { if (c[p.status] !== undefined) c[p.status]++; });
    return c;
  }, [proposals]);

  // submission counts (using created_at)
  const periodCounts = useMemo(() => {
    const now = Date.now();
    const dayMs = 86400000;
    let today = 0, week = 0, month = 0;
    proposals.forEach((p) => {
      const ts = p.created_at ? new Date(p.created_at).getTime() : 0;
      if (!ts) return;
      const ageDays = (now - ts) / dayMs;
      if (ageDays <= 1) today++;
      if (ageDays <= 7) week++;
      if (ageDays <= 30) month++;
    });
    return { today, week, month };
  }, [proposals]);

  const myDrafts = useMemo(() => {
    return proposals
      .filter((p) => p.status === 'draft')
      .sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || ''));
  }, [proposals]);

  const newProposal = async () => {
    const p = {
      id: 'p_' + Date.now(),
      title: 'New Proposal',
      status: 'draft',
      clientId: null,
      date: new Date().toISOString().slice(0, 10),
      validity: '30 days', version: '1', number: '',
      isSA: true,
      scope: [{ phase: 'Phase 1', task: '', days: '' }],
      tech: [{ item: '' }],
      pricing: { currency: 'INR', rows: [{ desc: '', amount: 0 }], discount: 0, tax: 0, final: 0, terms: '50% advance + 50% on delivery', note: '' },
      sections: {
        confidentiality: 'The information contained in this document is confidential and proprietary to Dextra Technologies.',
        acknowledgment: 'We would like to thank the client for giving Dextra Technologies the opportunity to submit this proposal.',
        disclaimer: 'The obligation of the parties to perform the effort identified in this document is subject to the execution of a written agreement.',
        about: 'Dextra Technologies is leading Software Solutions, Services & Products Company based in Chennai, India.',
        background: 'Dextra Technologies welcomes the opportunity to submit this proposal.',
        assumptions: ['Work will be done offsite at Dextra\'s office.', 'Client will provide required content and access.'],
        notInScope: ['Data Migration', 'Performance Testing'],
        whyDextra: 'Dextra Technologies — established 2015, Chennai.',
        portfolio: 'Sample works available at www.dextratechnologies.com',
      },
    };
    try {
      await axios.post(`${API_URL}/api/proposals`, p, { withCredentials: true });
      navigate(`/proposals/builder?id=${p.id}`);
    } catch (_) { alert('Could not create proposal'); }
  };

  return (
    <div data-testid="proposals-staff-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>Proposal Overview</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>Track your proposals across the workflow</p>
        </div>
        <Button className="text-white" style={{ background: 'var(--dx-primary)' }} onClick={newProposal} data-testid="new-proposal-btn">
          <Plus size={18} className="mr-1" /> New Proposal
        </Button>
      </div>

      {/* Period stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-5">
          <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--dx-text3)' }}>Today</div>
          <div className="text-3xl font-bold mt-1" style={{ color: 'var(--dx-text)' }} data-testid="count-today">{periodCounts.today}</div>
          <div className="text-xs mt-1" style={{ color: 'var(--dx-text2)' }}>proposals created today</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--dx-text3)' }}>This Week</div>
          <div className="text-3xl font-bold mt-1" style={{ color: 'var(--dx-text)' }} data-testid="count-week">{periodCounts.week}</div>
          <div className="text-xs mt-1" style={{ color: 'var(--dx-text2)' }}>last 7 days</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--dx-text3)' }}>This Month</div>
          <div className="text-3xl font-bold mt-1" style={{ color: 'var(--dx-text)' }} data-testid="count-month">{periodCounts.month}</div>
          <div className="text-xs mt-1" style={{ color: 'var(--dx-text2)' }}>last 30 days</div>
        </Card>
      </div>

      {/* Status cards */}
      <div>
        <h2 className="text-sm font-semibold mb-3 uppercase tracking-wider" style={{ color: 'var(--dx-text3)' }}>By Status</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Object.entries(STATUS_META).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <Card key={key} className="p-4" data-testid={`status-${key}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: meta.bg }}>
                    <Icon size={18} style={{ color: meta.color }} weight="fill" />
                  </div>
                </div>
                <div className="text-2xl font-bold" style={{ color: meta.color }}>{counts[key]}</div>
                <div className="text-xs font-semibold mt-0.5" style={{ color: 'var(--dx-text2)' }}>{meta.label}</div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* My drafts (only drafts staff can still edit) */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold" style={{ color: 'var(--dx-text)' }}>Your Drafts</h2>
          <span className="text-xs" style={{ color: 'var(--dx-text2)' }}>{myDrafts.length} pending</span>
        </div>
        {loading ? (
          <div className="text-sm py-4 text-center" style={{ color: 'var(--dx-text3)' }}>Loading...</div>
        ) : myDrafts.length === 0 ? (
          <div className="text-sm py-6 text-center" style={{ color: 'var(--dx-text3)' }}>
            No drafts in progress. Click <strong>+ New Proposal</strong> to start one.
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: 'var(--dx-border)' }}>
            {myDrafts.slice(0, 10).map((p) => (
              <li
                key={p.id}
                onClick={() => navigate(`/proposals/builder?id=${p.id}`)}
                data-testid={`draft-${p.id}`}
                className="py-3 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded transition-colors flex items-center gap-3"
              >
                <FileText size={16} style={{ color: 'var(--dx-text3)' }} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate" style={{ color: 'var(--dx-text)' }}>{p.title || 'Untitled'}</div>
                  <div className="text-xs" style={{ color: 'var(--dx-text2)' }}>
                    {p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </div>
                </div>
                <ArrowRight size={14} style={{ color: 'var(--dx-text3)' }} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default ProposalsStaffOverviewPage;
